import cx from "clsx";
import { Artwork } from "kreate-common/modules/business-types";
import React from "react";

import MethodAuctionEnglish from "./containers/MethodAuctionEnglish";
import MethodMarketplace from "./containers/MethodMarketplace";
import MethodMintingRulesV1 from "./containers/MethodMintingRulesV1";
import { analyzeArtwork$MethodMintingRulesV1 } from "./containers/MethodMintingRulesV1/utils";
import MethodOnDemandMinting from "./containers/MethodOnDemandMinting";
import { analyzeArtwork$MethodOnDemandMinting } from "./containers/MethodOnDemandMinting/utils";
import MethodReservationBasedMinting from "./containers/MethodReservationBasedMinting";
import styles from "./index.module.scss";
import { getPrimaryAcquisitionMethod } from "./utils";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  as?: "div" | "section";
  artwork: Artwork | null | undefined;
  nullIfCannotAcquire: boolean;
  nullable: true;
};

export default function PanelPrimaryAcquisition({
  className,
  style,
  as,
  artwork,
  nullIfCannotAcquire,
}: Props) {
  const Component = as || "div";
  const primaryAcquisitionMethod = getPrimaryAcquisitionMethod(artwork);

  if (!artwork || !primaryAcquisitionMethod) {
    return undefined;
  }

  const canAcquire = (): boolean => {
    switch (primaryAcquisitionMethod) {
      case "method-minting-rules-v1": {
        const info = analyzeArtwork$MethodMintingRulesV1(artwork);
        return !info.stats.isSoldOut;
      }
      case "method-on-demand-minting": {
        const info = analyzeArtwork$MethodOnDemandMinting(artwork);
        return !info.stats.isSoldOut;
      }
      case "method-marketplace": {
        const numEditions =
          artwork?.utxo?.datum?.type === "marketplace"
            ? artwork?.utxo?.otherAssets[0]?.tokens[0]?.tokenValue || 0
            : undefined;
        return !!numEditions;
      }
      case "method-reservation-based-minting": {
        const datumArtwork =
          artwork?.utxo?.datum.type === "artwork"
            ? artwork.utxo.datum.value
            : undefined;
        const isFull =
          datumArtwork?.numMinted != null &&
          datumArtwork?.numMinted === datumArtwork?.numEditions;
        return !isFull;
      }
      case "method-auction-english": {
        return false;
      }
    }
  };

  if (nullIfCannotAcquire && !canAcquire()) {
    return undefined;
  }

  return (
    <Component className={cx(styles.container, className)} style={style}>
      {(() => {
        switch (primaryAcquisitionMethod) {
          case "method-minting-rules-v1":
            if (
              nullIfCannotAcquire &&
              analyzeArtwork$MethodMintingRulesV1(artwork).stats.isSoldOut
            ) {
              return undefined;
            } else {
              return <MethodMintingRulesV1 artwork={artwork} />;
            }
          case "method-on-demand-minting":
            return <MethodOnDemandMinting artwork={artwork} />;
          case "method-marketplace":
            return <MethodMarketplace artwork={artwork} />;
          case "method-reservation-based-minting":
            return <MethodReservationBasedMinting artwork={artwork} />;
          case "method-auction-english":
            return <MethodAuctionEnglish artwork={artwork} />;
        }
      })()}
    </Component>
  );
}
